import { useCallback } from "react";

import { useNavigate } from "react-router-dom";

import { type CTAKind, fillCtaPath } from "./signalCTAs";

interface CtaAccount {
  account_id: string;
  account_name: string;
}

/**
 * Where a signal CTA (or an Action Guide step) takes the user: the
 * assessment modal in place, the Relationships page, an admin-registered
 * path, or the Account Synopsis. Shared by ActionCard, CompactActionCard
 * and SignalGuideModal so every entry point routes the same way.
 */
export const useSignalCtaRouter = (
  withOrg: (path: string) => string,
  onOpenAssess: (account: { id: string; name: string }) => void
) => {
  const navigate = useNavigate();
  return useCallback(
    (
      card: CtaAccount,
      kind: CTAKind,
      path?: string | null,
      /** Opens the Pre-call Brief in place, where the caller can. */
      openBrief?: () => void
    ) => {
      if (kind === "brief" && openBrief) {
        openBrief();
        return;
      }
      if (kind === "path" && path) {
        navigate(withOrg(fillCtaPath(path, card.account_id)));
        return;
      }
      switch (kind) {
        case "assess":
          onOpenAssess({ id: card.account_id, name: card.account_name });
          return;
        case "relationships":
          navigate(
            withOrg(
              `/relationship?tab=account-relationships&accountId=${encodeURIComponent(card.account_id)}`
            )
          );
          return;
        case "synopsis":
        default:
          navigate(withOrg(`/account-synopsis/${card.account_id}`), {
            state: {
              from: withOrg("/my-portfolio-accounts"),
              fromLabel: "My Accounts",
            },
          });
      }
    },
    [navigate, withOrg, onOpenAssess]
  );
};
